import { createApi } from "@reduxjs/toolkit/query/react";
import { DynamicBaseQuery } from "./baseQuery";
import { CreateBodyMapEntryRequest } from "./types/bodyMap/requests/CreateBodyMapEntryRequest";
import { BodyMapEntryModel } from "./types/bodyMap/models/BodyMapEntryModel";
import { BodyMapEntriesResponse } from "./types/bodyMap/responses/BodyMapEntriesResponse";
import { BodyMapEntryDetailedModel } from "./types/bodyMap/models/BodyMapEntryDetailedModel";
import { SuccessResponse } from "./types/common/responses/SuccessResponse";
import { BodyMapEntriesRequest } from "./types/bodyMap/requests/BodyMapEntriesRequest";

export const bodyMapApi = createApi({
  reducerPath: "bodyMapApi",
  baseQuery: DynamicBaseQuery,
  tagTypes: ["BodyMapEntries", "BodyMapEntry"],
  endpoints: (builder) => ({
    getBodyMapEntries: builder.query<
      BodyMapEntriesResponse,
      BodyMapEntriesRequest
    >({
      query: ({ residentId, filter, page }) => ({
        url: `/residents/${residentId}/body-map-entries`,
        method: "GET",
        params: {
          "filter[status]": filter?.status,
          page,
        },
      }),
      providesTags: ["BodyMapEntries"],
    }),
    getBodyMapEntry: builder.query<
      BodyMapEntryDetailedModel,
      { residentId: number; entryId: number }
    >({
      query: ({ residentId, entryId }) => ({
        url: `/residents/${residentId}/body-map-entries/${entryId}`,
        method: "GET",
      }),
      providesTags: (_result, _error, { residentId, entryId }) => [
        { type: "BodyMapEntry", id: `${residentId}-${entryId}` },
      ],
    }),
    createBodyMapEntry: builder.mutation<
      BodyMapEntryModel,
      { residentId: number; body: CreateBodyMapEntryRequest }
    >({
      query: ({ residentId, body }) => ({
        url: `/residents/${residentId}/body-map-entries`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["BodyMapEntries"],
    }),
    uploadBodyMapEntryMedia: builder.mutation<
      void,
      {
        residentId: number;
        entryId: number;
        formData: FormData;
      }
    >({
      query: ({ residentId, entryId, formData }) => {
        return {
          url: `/residents/${residentId}/body-map-entries/${entryId}/media`,
          method: "POST",
          body: formData,
        };
      },
    }),
    updateBodyMapEntryWithLogs: builder.mutation<
      SuccessResponse,
      {
        residentId: number;
        entryId: number;
        formData: FormData;
      }
    >({
      query: ({ residentId, entryId, formData }) => ({
        url: `/residents/${residentId}/body-map-entries/${entryId}/logs`,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: (_result, _error, { residentId, entryId }) => [
        { type: "BodyMapEntry", id: `${residentId}-${entryId}` },
      ],
    }),
    resolveBodyMapEntry: builder.mutation<
      SuccessResponse,
      {
        residentId: number;
        entryId: number;
      }
    >({
      query: ({ residentId, entryId }) => ({
        url: `/residents/${residentId}/body-map-entries/${entryId}/resolve`,
        method: "POST",
      }),
      invalidatesTags: ["BodyMapEntries"],
    }),
  }),
});
export const {
  useGetBodyMapEntriesQuery,
  useLazyGetBodyMapEntryQuery,
  useCreateBodyMapEntryMutation,
  useUploadBodyMapEntryMediaMutation,
  useUpdateBodyMapEntryWithLogsMutation,
  useResolveBodyMapEntryMutation,
} = bodyMapApi;
